import { CustomFieldRetrieveReadable, Type83fEnum } from '@crm/api'
import { DEFAULT_ISO_DATE_FORMAT } from '@shared/utils'
import { AnyObject } from 'antd/es/_util/type'
import { Rule } from 'antd/es/form'
import { DefaultOptionType } from 'antd/es/select'
import dayjs from 'dayjs'

import { formatDateToDayjs } from './date'

/**
 * Transforms form data with custom field values to the API format
 * @param formData - The form data containing custom_field_values
 * @param customFields - Array of custom field definitions
 * @returns Array of custom field values in API format
 */
export const transformCustomFieldValues = (
  customFieldValues: AnyObject,
  customFields: CustomFieldRetrieveReadable[]
) => {
  return Object.entries(customFieldValues || {})
    .filter(([, value]) => {
      return value !== undefined && value !== null && value !== ''
    })
    .map(([fieldId, value]) => {
      const fieldIdNumber = parseInt(fieldId, 10)
      const customField = customFields.find(
        (field) => field.id === fieldIdNumber
      )

      if (!customField) {
        console.warn(`Custom field with ID ${fieldId} not found`)
        return null
      }

      let transformedValue = value

      if (customField.type === 'date' && value) {
        transformedValue =
          typeof value === 'object' &&
          'format' in value &&
          typeof value.format === 'function'
            ? value.format(DEFAULT_ISO_DATE_FORMAT)
            : value
      } else if (customField.type === 'multiselect' && Array.isArray(value)) {
        transformedValue = value
      }

      // Build values array depending on field type
      let valuesArray:
        | Array<{ value: unknown }>
        | Array<{ label: string; value: string }>
      if (
        customField.type === Type83fEnum.SELECT ||
        customField.type === Type83fEnum.MULTISELECT
      ) {
        const toLabelValue = (v: unknown): { label: string; value: string } => {
          // If already an object with label/value, keep it
          if (
            v &&
            typeof v === 'object' &&
            'value' in (v as { value?: unknown })
          ) {
            const obj = v as { label: string; value: string }

            return {
              label: obj.label,
              value: obj.value
            }
          }

          const str = String(v ?? '')
          const found = (customField.options || []).find(
            (opt) => opt?.label === str
          )

          return {
            label: found?.label || '',
            value: found?.value || ''
          }
        }

        valuesArray = Array.isArray(transformedValue)
          ? transformedValue?.map(toLabelValue)
          : [toLabelValue(transformedValue)]
      } else {
        valuesArray = Array.isArray(transformedValue)
          ? transformedValue?.map((v) => ({ value: v }))
          : [{ value: transformedValue }]
      }

      return {
        field_id: fieldIdNumber,
        field_key_en: customField.key_en,
        field_key_ro: customField.key_ro,
        field_key_ru: customField.key_ru,
        field_key_it: customField.key_it,
        field_type: customField.type,
        values: valuesArray
      }
    })
    .filter(Boolean)
}

/**
 * Transforms custom field values back to form format
 * @param customFieldValues - Array of custom field values from API
 * @returns Object with field IDs as keys and values as values
 */
export const transformCustomFieldValuesToForm = (
  customFieldValues: Array<{
    field_id: number
    field_type: string
    values: Array<{ value?: string }>
    field_key_en?: string
    field_key_ro?: string
    field_key_ru?: string
    field_key_it?: string
  }>
) => {
  const formData: Record<string, unknown> = {}

  customFieldValues?.forEach((fieldValue) => {
    const { field_id, field_type, values } = fieldValue

    if (values.length === 0) return

    if (field_type === Type83fEnum.SELECT) {
      const value = values[0] as unknown as DefaultOptionType
      formData[field_id] = value?.label
    } else if (field_type === Type83fEnum.MULTISELECT) {
      const valuesArray = values as unknown as DefaultOptionType[]
      formData[field_id] = valuesArray?.map((v) => v?.label).filter(Boolean)
    } else if (field_type === Type83fEnum.DATE) {
      formData[field_id] = values[0].value
        ? formatDateToDayjs(values[0].value)
        : undefined
    } else {
      formData[field_id] = values[0].value || ''
    }
  })

  return formData
}

export const getLabel = (
  props: Partial<CustomFieldRetrieveReadable>,
  currentLang: string
) => {
  const label = (props?.[`key_${currentLang}` as keyof typeof props] ||
    props?.key_en ||
    props?.key_ro ||
    props?.key_ru ||
    props?.key_it) as string

  return label
}

export const getValidationRules = (
  props: Partial<CustomFieldRetrieveReadable>
): Rule[] => {
  return [
    {
      required: props?.is_required ?? false
    },
    ...(props?.type === Type83fEnum.NUMBER
      ? [
          {
            min: props?.validation_rules?.min,
            max: props?.validation_rules?.max,
            type: 'number' as const
          }
        ]
      : []),
    ...(props?.validation_rules?.pattern
      ? [
          {
            pattern: new RegExp(props?.validation_rules?.pattern)
          }
        ]
      : [])
  ]
}

export const getInitialValue = (
  props: Partial<CustomFieldRetrieveReadable>
) => {
  if (props?.type === Type83fEnum.DATE) {
    return getDateInitialValue(props)
  }

  if (props?.type === Type83fEnum.SELECT) {
    return (props?.default_value as DefaultOptionType)?.label
  }

  if (props?.type === Type83fEnum.MULTISELECT) {
    return (props?.default_value as DefaultOptionType[])?.map(
      (item) => item?.label
    )
  }

  return props?.default_value
}

export const getDateInitialValue = (
  props: Partial<CustomFieldRetrieveReadable>
) => {
  switch (props?.default_value) {
    case 'yesterday':
      return dayjs().subtract(1, 'day')
    case 'today':
      return dayjs()
    case 'tomorrow':
      return dayjs().add(1, 'day')
    case 'week_after':
      return dayjs().add(1, 'week')
    case 'two_weeks_after':
      return dayjs().add(2, 'week')
    case 'month_after':
      return dayjs().add(1, 'month')
    case 'three_months_after':
      return dayjs().add(2, 'month')
    default:
      return undefined
  }
}
